import { useState, useRef, useEffect } from "react";
import { Code, Smartphone, Zap } from "lucide-react";

import stepCreateVideo from "@/assets/videos/step-create.mp4";
import stepShareVideo from "@/assets/videos/step-share.mp4";
import stepNotifyVideo from "@/assets/videos/step-notify.mp4";

const badges = [
  { icon: Code, text: "Pas de code" },
  { icon: Smartphone, text: "Pas d'App Store" },
  { icon: Zap, text: "Pas de complexité" },
];

const steps = [
  {
    title: "Créez votre carte",
    description:
      "Créez une carte professionnelle à votre image en quelques minutes. Notre éditeur visuel vous permet de tout personnaliser : logo, couleurs, informations clés. L'aperçu en temps réel vous montre exactement ce que verra votre audience.",
    video: stepCreateVideo,
  },
  {
    title: "Partagez votre carte",
    description:
      "Un lien. Un QR code. C'est tout ce qu'il vous faut. Partagez sur vos réseaux, dans vos emails, ou en live. Votre audience ajoute la carte à son Wallet en un tap. Sans téléchargement. Sans inscription. Sans friction.",
    video: stepShareVideo,
  },
  {
    title: "Communiquez directement",
    description:
      "Une annonce importante ? Tapez votre message et envoyez. Votre notification apparaît instantanément sur l'écran verrouillé de tous vos abonnés. Visibilité garantie, action immédiate. Parfait pour vos lancements, événements ou nouveautés.",
    video: stepNotifyVideo,
    hasLink: true,
  },
];

export function HowItWorks() {
  const [isMounted, setIsMounted] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !headerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  return (
    <section id="comment-ca-marche" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
        >
          {/* Titre principal */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-6 text-[#074155]">
            Lancez votre propre canal marketing{" "}
            <span className="text-[#0C94A6]">en quelques minutes</span>
          </h2>

          {/* Badges */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
            {badges.map((badge, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#0c97a9] bg-[#0C94A6]/15"
              >
                <badge.icon className="w-4 h-4 text-[#0c97a9]" strokeWidth={2} />
                <span className="text-sm font-semibold text-[#0c97a9]">{badge.text}</span>
              </div>
            ))}
          </div>

          {/* Sous-titre */}
          <p className="text-base sm:text-lg font-semibold text-[#4f5b5d] max-w-xl mx-auto">
            Trois étapes pour une ligne directe avec votre audience.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-0">
          {steps.map((step, index) => (
            <StepItem key={index} step={step} index={index} isLast={index === steps.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface StepItemProps {
  step: {
    title: string;
    description: string;
    video: string;
    hasLink?: boolean;
  };
  index: number;
  isLast: boolean;
}

function StepItem({ step, index, isLast }: StepItemProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const stepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !stepRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(stepRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  const handleScrollToFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById("fonctionnalites");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div ref={stepRef} className="flex">
      {/* Colonne Timeline */}
      <div className="flex flex-col items-center mr-4 sm:mr-6 lg:mr-8">
        {/* Cercle numéro */}
        <div
          className={`w-10 h-10 rounded-full bg-[#0C94A6] flex items-center justify-center flex-shrink-0 transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"
          }`}
        >
          <span className="text-white font-bold text-sm">0{index + 1}</span>
        </div>

        {/* Ligne verticale */}
        {!isLast && (
          <div
            className={`w-0.5 flex-1 bg-[#0C94A6]/20 origin-top transition-transform duration-700 ease-out ${
              isVisible ? "scale-y-100" : "scale-y-0"
            }`}
          />
        )}
      </div>

      {/* Colonne Contenu */}
      <div
        className={`flex-1 pb-12 lg:pb-16 transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        }`}
      >
        {/* Titre */}
        <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3">{step.title}</h3>

        {/* Description */}
        <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-6">
          {step.description}
          {step.hasLink && (
            <>
              {" "}
              <a
                href="#fonctionnalites"
                onClick={handleScrollToFeatures}
                className="font-semibold text-[#0C94A6] hover:underline"
              >
                Découvrez toutes les fonctionnalités des notifications push.
              </a>
            </>
          )}
        </p>

        {/* Vidéo - lazy loading pour économiser ~7Mo au chargement initial */}
        <div
          className="overflow-hidden rounded-2xl"
          style={{
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <video
            src={isVisible ? step.video : undefined}
            autoPlay={isVisible}
            loop
            playsInline
            muted
            preload="none"
            className="w-full h-auto"
            style={{ aspectRatio: "16/9", backgroundColor: "hsl(var(--muted))" }}
          />
        </div>
      </div>
    </div>
  );
}
